import React, { useEffect, useState } from "react";
import { Send, Loader2, Mail, ExternalLink, AlertCircle, CheckCircle2, Upload } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * AdminBroadcast — super-admin only "Send launch email" tool.
 *
 * Lets the primary admin paste/upload a recipient list, pick a saved
 * template, customize the subject + From / Reply-to addresses, and blast
 * via Resend. Designed so transactional app emails (welcomes, password
 * resets) keep using their existing verified sender — this page sets a
 * separate marketing From so replies can land in the theporchlight.io
 * inbox instead of the platform owner's personal inbox.
 *
 * Confirmation gate: clicking "Send" doesn't fire until the admin types the
 * recipient count into a confirm box, so a misclick never blasts a list.
 */
export default function AdminBroadcast() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("launch");
  const [subject, setSubject] = useState("");
  const [abTest, setAbTest] = useState(false);
  const [subjectB, setSubjectB] = useState("");
  const [fromEmail, setFromEmail] = useState("hello@theporchlight.io");
  const [replyTo, setReplyTo] = useState("hello@theporchlight.io");
  const [fromName, setFromName] = useState("PorchLight");
  const [recipientsRaw, setRecipientsRaw] = useState("");
  const [confirmCount, setConfirmCount] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewSent, setPreviewSent] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Fetch the template registry once on mount so the form reflects every
  // template the backend supports (we only ship "launch" today, but the
  // dropdown is wired so adding "follow_up" later is one-line).
  useEffect(() => {
    api.get("/admin/marketing/broadcast/templates")
      .then(({ data }) => {
        setTemplates(data?.templates || []);
        const first = (data?.templates || [])[0];
        if (first) {
          setTemplateId(first.id);
          setSubject(first.default_subject || "");
          setSubjectB(first.default_subject_b || "");
        }
      })
      .catch(() => {});
  }, []);

  // When the user changes templates, swap in that template's default subject
  // unless they've already manually edited the field.
  const onTemplateChange = (id) => {
    const t = templates.find((x) => x.id === id);
    setTemplateId(id);
    if (t?.default_subject) setSubject(t.default_subject);
    setSubjectB(t?.default_subject_b || "");
    // Turn A/B off automatically when switching to a template that doesn't support it.
    if (!t?.ab_test_supported) setAbTest(false);
  };

  // Parse the textarea on the fly so we can show a live "X valid recipients"
  // count next to the confirmation field. We mirror the backend's regex
  // (loose RFC-5322 subset that's strict enough for marketing lists).
  const parseRecipients = (raw) => {
    const re = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
    const seen = new Set();
    const valid = [];
    const invalid = [];
    raw.split(/[\s,;]+/).forEach((part) => {
      const e = (part || "").trim().toLowerCase();
      if (!e) return;
      if (!re.test(e)) {
        invalid.push(e);
        return;
      }
      if (seen.has(e)) return;
      seen.add(e);
      valid.push(e);
    });
    return { valid, invalid };
  };

  const { valid: validRecipients, invalid: invalidRecipients } = parseRecipients(recipientsRaw);
  const recipientCount = validRecipients.length;
  const confirmMatches = String(confirmCount).trim() === String(recipientCount) && recipientCount > 0;
  const currentTemplate = templates.find((t) => t.id === templateId);
  const abSupported = !!currentTemplate?.ab_test_supported;
  const abBlockedByEmptyB = abTest && !subjectB.trim();

  const onCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setRecipientsRaw((prev) => (prev ? prev + "\n" + text : text));
    e.target.value = "";
  };

  const send = async () => {
    setError("");
    setResult(null);
    if (!confirmMatches) {
      setError("Type the recipient count exactly to confirm.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/marketing/broadcast", {
        template_id: templateId,
        subject: subject.trim(),
        from_email: fromEmail.trim(),
        reply_to: replyTo.trim(),
        from_name: fromName.trim(),
        recipients: validRecipients,
        ab_test: abTest,
        subject_b: abTest ? subjectB.trim() : "",
      });
      setResult(data);
      setConfirmCount("");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Send failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const sendPreview = async () => {
    setError("");
    setPreviewSent("");
    if (!subject.trim() || !fromEmail.trim() || !replyTo.trim()) {
      setError("Fill in subject + From email + Reply-to before previewing.");
      return;
    }
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/admin/marketing/broadcast/preview", {
        template_id: templateId,
        subject: subject.trim(),
        from_email: fromEmail.trim(),
        reply_to: replyTo.trim(),
        from_name: fromName.trim(),
      });
      setPreviewSent(data?.sent_to || "your inbox");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Preview send failed. Try again.");
    } finally {
      setPreviewBusy(false);
    }
  };

  // Primary-admin gate (server enforces too).
  if (user && !user.is_primary_admin) {
    return (
      <div className="container-x" style={{ padding: "64px 0" }} data-testid="admin-broadcast-403">
        <h1 className="font-serif" style={{ fontSize: 32 }}>Not authorized</h1>
        <p style={{ color: "var(--text-muted)" }}>This tool is reserved for the platform owner.</p>
      </div>
    );
  }

  return (
    <div className="container-x" style={{ padding: "32px 0 96px", maxWidth: 880 }} data-testid="admin-broadcast-page">
      <div style={{ marginBottom: 28 }}>
        <span className="overline">Super-admin</span>
        <h1 className="font-serif" style={{ fontSize: "clamp(28px, 4vw, 40px)", margin: "6px 0 6px", fontWeight: 500, letterSpacing: "-0.01em" }}>
          Send a broadcast email
        </h1>
        <p style={{ color: "var(--text-muted)", maxWidth: 640, margin: 0 }}>
          Blast a marketing email to a custom list. Uses your domain inbox for both sending and replies — your transactional app emails keep their existing verified sender.
        </p>
      </div>

      {/* Template + subject */}
      <Card title="1. Email content">
        <Field label="Template">
          <select
            data-testid="broadcast-template"
            className="input-field"
            value={templateId}
            onChange={(e) => onTemplateChange(e.target.value)}
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Subject line">
          <input
            data-testid="broadcast-subject"
            className="input-field"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Your sellers keep asking &ldquo;what&rsquo;s next?&rdquo; &mdash; here&rsquo;s the fix."
          />
        </Field>
        {abSupported && (
          <div data-testid="broadcast-ab-block" style={{ background: "var(--surface-alt)", border: "1px solid var(--border)", borderRadius: 10, padding: "14px 16px", margin: "10px 0 6px" }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                data-testid="broadcast-ab-toggle"
                checked={abTest}
                onChange={(e) => setAbTest(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>
                  A/B test subject lines
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, lineHeight: 1.5 }}>
                  Splits recipients 50/50 between subject A (above) and subject B (below). Tracks opens
                  via a 1×1 pixel and clicks via `utm_content` — see the winner in <b>Admin → Insights</b> after sends land.
                </div>
              </div>
            </label>
            {abTest && (
              <div style={{ marginTop: 12 }}>
                <Field label="Subject line B (variant)">
                  <input
                    data-testid="broadcast-subject-b"
                    className="input-field"
                    value={subjectB}
                    onChange={(e) => setSubjectB(e.target.value)}
                    placeholder="You&rsquo;re not the mess. Your process is."
                  />
                </Field>
                {abBlockedByEmptyB && (
                  <p data-testid="broadcast-ab-empty-warn" style={{ fontSize: 12, color: "var(--secondary)", margin: "4px 0 0" }}>
                    Subject B can&rsquo;t be empty while A/B testing is on.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <a
            data-testid="broadcast-preview-link"
            href="/email-preview.html"
            target="_blank"
            rel="noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, color: "var(--primary)", textDecoration: "none", borderBottom: "1px dashed var(--border)" }}
          >
            Preview the email <ExternalLink size={13} />
          </a>
          <button
            type="button"
            data-testid="broadcast-send-preview-btn"
            onClick={sendPreview}
            disabled={previewBusy}
            className="btn-secondary"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}
          >
            {previewBusy ? <><Loader2 size={13} className="spin" /> Sending preview…</> : <><Mail size={13} /> Send myself a preview</>}
          </button>
          {previewSent && (
            <span data-testid="broadcast-preview-sent" style={{ fontSize: 13, color: "var(--primary)", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <CheckCircle2 size={14} /> Sent to {previewSent}
            </span>
          )}
        </div>
      </Card>

      {/* From + reply-to */}
      <Card title="2. Sender" hint="Verified at theporchlight.io in Resend. Replies land in your IONOS inbox.">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: 14 }}>
          <Field label="From name">
            <input
              data-testid="broadcast-from-name"
              className="input-field"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="PorchLight"
            />
          </Field>
          <Field label="From email">
            <input
              data-testid="broadcast-from-email"
              className="input-field"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="hello@theporchlight.io"
            />
          </Field>
        </div>
        <Field label="Reply-to email">
          <input
            data-testid="broadcast-reply-to"
            className="input-field"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            placeholder="hello@theporchlight.io"
          />
        </Field>
      </Card>

      {/* Recipients */}
      <Card
        title="3. Recipients"
        hint="One email per line. Paste from a spreadsheet or upload a CSV. Duplicates and invalid addresses are stripped automatically."
      >
        <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <label
            className="btn-secondary"
            style={{ cursor: "pointer", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Upload size={14} /> Upload CSV
            <input
              data-testid="broadcast-csv-upload"
              type="file"
              accept=".csv,.txt"
              style={{ display: "none" }}
              onChange={onCsvUpload}
            />
          </label>
          {recipientsRaw && (
            <button
              type="button"
              data-testid="broadcast-clear"
              onClick={() => setRecipientsRaw("")}
              className="btn-ghost"
              style={{ fontSize: 13 }}
            >
              Clear list
            </button>
          )}
        </div>
        <textarea
          data-testid="broadcast-recipients"
          className="input-field"
          rows={10}
          value={recipientsRaw}
          onChange={(e) => setRecipientsRaw(e.target.value)}
          placeholder={"jane@example.com\nbob@example.com\n…"}
          style={{ resize: "vertical", fontFamily: "ui-monospace, monospace", fontSize: 13 }}
        />
        <div data-testid="broadcast-counts" style={{ marginTop: 10, display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13 }}>
          <span style={{ color: "var(--primary)", fontWeight: 600 }}>
            {recipientCount} valid recipient{recipientCount === 1 ? "" : "s"}
          </span>
          {invalidRecipients.length > 0 && (
            <span style={{ color: "var(--secondary)" }}>
              {invalidRecipients.length} ignored ({invalidRecipients.slice(0, 3).join(", ")}{invalidRecipients.length > 3 ? "…" : ""})
            </span>
          )}
        </div>
      </Card>

      {/* Send */}
      <Card title="4. Send">
        <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 14px" }}>
          Type <strong>{recipientCount || "0"}</strong> to confirm you want to send to <strong>{recipientCount}</strong> recipient{recipientCount === 1 ? "" : "s"}.
        </p>
        <Field label="Confirm recipient count">
          <input
            data-testid="broadcast-confirm"
            className="input-field"
            value={confirmCount}
            onChange={(e) => setConfirmCount(e.target.value)}
            placeholder="0"
            style={{ maxWidth: 200 }}
          />
        </Field>
        {error && (
          <div data-testid="broadcast-error" style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 14, color: "var(--secondary)", margin: "12px 0" }}>
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            {error}
          </div>
        )}
        <button
          type="button"
          data-testid="broadcast-send-btn"
          onClick={send}
          className="btn-primary"
          disabled={busy || !confirmMatches || abBlockedByEmptyB}
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          {busy ? <><Loader2 size={14} className="spin" /> Sending {recipientCount}…</> : <><Send size={14} /> Send to {recipientCount} recipient{recipientCount === 1 ? "" : "s"}</>}
        </button>
        <p style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 12 }}>
          Sends are sequential with an 80ms delay so we stay under Resend's rate limit. Allow up to a minute for 500 recipients.
        </p>
      </Card>

      {result && (
        <div
          data-testid="broadcast-result"
          style={{
            marginTop: 22, padding: 22,
            background: result.failed_count === 0 ? "rgba(43, 76, 63, 0.06)" : "rgba(157, 65, 51, 0.06)",
            border: `1px solid ${result.failed_count === 0 ? "rgba(43, 76, 63, 0.18)" : "rgba(157, 65, 51, 0.2)"}`,
            borderRadius: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            {result.failed_count === 0 ? (
              <CheckCircle2 size={20} color="var(--primary)" />
            ) : (
              <AlertCircle size={20} color="var(--secondary)" />
            )}
            <h3 className="font-serif" style={{ fontSize: 20, margin: 0, fontWeight: 500 }}>
              {result.failed_count === 0 ? "Sent!" : "Sent with some failures"}
            </h3>
          </div>
          <p style={{ fontSize: 14, color: "var(--text-muted)", margin: "0 0 8px" }}>
            <strong>{result.sent_count}</strong> sent ·{" "}
            <strong>{result.failed_count}</strong> failed ·{" "}
            <strong>{result.invalid_count}</strong> invalid
          </p>
          {result.ab_test && result.variant_counts && (
            <p data-testid="broadcast-ab-split-result" style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 8px" }}>
              A/B split — <strong>{result.variant_counts.a || 0}</strong> got subject A,{" "}
              <strong>{result.variant_counts.b || 0}</strong> got subject B. Check{" "}
              <a href="/admin/insights" style={{ color: "var(--primary)" }}>Insights</a> for the winner once opens land.
            </p>
          )}
          {result.failed?.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 13, color: "var(--secondary)" }}>
                View {result.failed.length} failed sends
              </summary>
              <ul style={{ marginTop: 8, fontSize: 13, lineHeight: 1.55, color: "var(--text-muted)" }}>
                {result.failed.map((f, i) => (
                  <li key={i}><code>{f.email}</code> — {f.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Card({ title, hint, children }) {
  return (
    <section
      style={{
        marginBottom: 18,
        background: "#fff",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: "22px 24px",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <h2 className="font-serif" style={{ fontSize: 18, margin: 0, fontWeight: 500 }}>
          {title}
        </h2>
        {hint && <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--text)" }}>{label}</span>
      {children}
    </label>
  );
}
